import React, { useEffect, useRef, useState } from 'react';

import shipModelsImage from '@assets/img/arts/ship-models.png';
import blanketInsuranceIcon from '@assets/img/icons/blanket-insurance.svg';
import blanketInsuranceAccentIcon from '@assets/img/icons/blanket-insurance.svg';
import commoditiesAccentIcon from '@assets/img/icons/commodities-accent.svg';
import commoditiesIcon from '@assets/img/icons/commodities.svg';
import customsAccentIcon from '@assets/img/icons/customs-accent.svg';
import customsIcon from '@assets/img/icons/customs.svg';
import d2dLogo from '@assets/img/icons/d2d-white.png';
import bookingInquiryIcon from '@assets/img/icons/dashboard/booking-inquiry.svg';
import currentBookingsIcon from '@assets/img/icons/dashboard/current-bookings.svg';
import invoiceIcon from '@assets/img/icons/dashboard/invoice.svg';
import priceSimulatorIcon from '@assets/img/icons/dashboard/price-simulator.svg';
import deliveryAccentIcon from '@assets/img/icons/delivery-accent.svg';
import deliveryIcon from '@assets/img/icons/delivery.svg';
import gotoIcon from '@assets/img/icons/goto.svg';
import receiversAccentIcon from '@assets/img/icons/receivers-accent.svg';
import receiversIcon from '@assets/img/icons/receivers.svg';
import settingsAccentIcon from '@assets/img/icons/settings-accent.svg';
import settingsIcon from '@assets/img/icons/settings.svg';
import shippersAccentIcon from '@assets/img/icons/shippers-accent.svg';
import shippersIcon from '@assets/img/icons/shippers.svg';
import dashboardBanner from '@assets/videos/dashboard-banner.mp4';
import { ExpandMoreButton } from '@components/ExpandMoreButton';
import { HorizontalSpace } from '@components/HorizontalSpace';
import { Layout } from '@components/Layout';
import { MemberIntroDialog } from '@components/MemberIntroDialog';
import { CargoTrackingResultQuery } from '@components/utilities/CargoTracking/helpers';
import { NotificationBanner } from '@components/utilities/Dashboard/NotificationBanner';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { FAQItem as FAQItemData, faqStrings } from '@data/faqs';
import { instructionStrings } from '@data/instruction';
import { useCompany } from '@hooks/queries/useCompany';
import { useInstructionOverview } from '@hooks/queries/useInstructionOverview';
import { useUser } from '@hooks/queries/useUser';
import Collapse from '@material-ui/core/Collapse';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { createValidate, requiredStringField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { stringifySearch } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';
import { Field, Form, Formik } from 'formik';
import * as t from 'io-ts';
import { Link, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: 'ダッシュボード',
    priceSimulator: '料金シミュレーター',
    shippers: `${bookingInquiryStrings.shipper}一覧`,
    consignees: `${bookingInquiryStrings.consignee}一覧`,
    commodities: '輸入商品一覧',
    d2dHelpCenter: 'D2Dヘルプセンター',
    d2dMemberApplication: 'D2D MEMBERS申込',
    bookingInquiry: '予約申込',
    bookingManagement: '案件管理',
    cargoTracking: '貨物追跡',
    invoice: '請求書',
    accountSettings: 'アカウント設定',
    faqsTitle: 'よくある質問',
    nTh: (index: number) => `${index}、`,
    pendingMember: 'D2D MEMBERS開設中...',
    toolsTitle: '機能一覧',
    addStaffs: '担当スタッフ追加',
    customsInstruction: '通関書類のアップロード',
    deliveryInstruction: '納品の最終確認',
    trackNo: '追跡番号',
    track: '追跡',
    slogan: '次世代デジタルフォワーディングサービス',
    importTrackNoError: '追跡番号を入力してください。',
    searchMultipleTrackNos: '複数のY番号の追跡へ',
    notMemberYetForPending:
      '一生懸命D2D MEMBERSを開設中。新規取引手続きが完了するまでお待ちください。',
    functionIsLimitedForPending:
      'こちらはD2D MEMBERS限定機能です。新規取引手続きが完了するまでお待ちください。',
    notMemberYetForDraft:
      'あなたはまだD2D MEMBERSではありません。D2D MEMBERS限定機能をご利用いただくには、D2D MEMBERSへの申込をお願い致します。',
    functionIsLimitedForDraft:
      'こちらはD2D MEMBERS限定機能です。お申込みがまだの方はコチラからご確認ください。',
    d2dMembers: 'D2D MEMBERS',
    sharedInfo: 'D2D MEMBERS 共有情報',
    applyForMembersNow: '今すぐD2D MEMBERSに申込',
    goldenWeek2023Notification:
      '2023/04/17 【重要】ゴールデンウイークのスケジュールについて 【ご案内】平素よりD2Dサービスをご利用いただき、誠にありがとうございます。ゴールデンウイークのスケジュールは下記のとおり予定...',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  prompt: {
    ...common.normalText,
    fontSize: 44,
    fontWeight: 600,
    lineHeight: '75px',
    maxWidth: '70%',
    [theme.breakpoints.down('sm')]: {
      fontSize: 22,
      lineHeight: '36px',
      maxWidth: '100%',
    },
  },
  outer: {
    backgroundColor: theme.palette.background.paper,
    paddingBottom: '1.25rem',
  },
  outerFaq: {
    paddingTop: '1.25rem',
    backgroundColor: theme.palette.grey[100],
  },
  inner: {
    maxWidth: 1200,
    margin: 'auto',
    paddingBottom: '2rem',
  },
  item: {
    ...common.flexColumn,
    flex: 1,
    alignItems: 'center',
    color: theme.palette.grey['800'],
    backgroundColor: theme.palette.background.paper,
    padding: '36px 0',
    margin: 0,
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
    },
  },
  itemTitle: {
    ...common.normalText,
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      height: '55.5px',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
    },
  },
  itemImage: {
    width: 160,
    height: 160,
    [theme.breakpoints.down('sm')]: {
      width: '50%',
      height: '50%',
    },
  },
  items: {
    ...common.flexInline,
    ...common.horizontalSpace2,
    [theme.breakpoints.down('sm')]: {
      ...common.multilineContainer,
      ...common.horizontalSpace0,
      gap: theme.spacing(1),
    },
  },
  faqTitle: {
    ...common.normalText,
    color: theme.palette.text.primary,
    fontSize: 30,
    lineHeight: '42px',
    fontWeight: 600,
  },
  faqSection: {
    ...common.flexColumn,
    ...common.verticalSpace1,
    padding: 20,
    backgroundColor: theme.palette.grey[200],
    maxHeight: 600,
    overflowY: 'scroll',
  },
  faqItem: {
    ...common.flexColumn,
    ...common.middleText,
    backgroundColor: theme.palette.background.paper,
    padding: 20,
  },
  faqItemTitle: {
    ...common.flexInline,
    ...common.baseButton,
    color: theme.palette.grey['800'],
    fontWeight: 500,
    justifyContent: 'space-between',
  },
  faqItemContent: {
    color: theme.palette.grey['700'],
    whiteSpace: 'pre-wrap',
    margin: '1rem 0',
  },
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  illustrationContainer: {
    ...common.flexInline,
    ...common.justifyEnd,
    height: 0,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  shipModels: {
    ...common.backgroundCenter,
    height: 270,
    width: 360,
    position: 'relative',
    top: -10,
    backgroundImage: `url("${shipModelsImage}")`,
  },
  memberSpecials: {
    ...common.biggerText,
    ...common.textGray700,
    lineHeight: 2.5,
    [theme.breakpoints.down('sm')]: {
      fontSize: 20,
    },
  },
  memberSpecialsContainer: {
    maxWidth: '500px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: 'initial',
    },
  },
  dot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    marginBottom: 2,
    marginRight: 10,
    backgroundColor: theme.palette.grey[800],
    borderRadius: '50%',
  },
  section: {
    ...common.flexColumn,
  },
  toolItemContainer: {
    ...common.flexCenter,
    ...common.normalText,
    ...common.textGray800,
    ...common.baseButton,
    backgroundColor: theme.palette.common.white,
    padding: '20px 0',
    boxShadow: '0px 2px 8px rgba(200, 201, 204, 0.5)',
    transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
    '&:hover': {
      boxShadow: '0px 2px 8px rgba(200, 201, 204, 0.8)',
      transform: 'scale(0.9)',
    },
    '&$big': {
      padding: '45px 0',
    },
    '&$disabled': {
      opacity: '0.5',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  toolItemHover: {
    width: 20,
    height: 20,
    '$toolItemContainer &': {
      display: 'none',
    },
    '$toolItemContainer:hover &': {
      display: 'initial',
    },
  },
  toolItemNotHover: {
    width: 20,
    height: 20,
    '$toolItemContainer &': {
      display: 'initial',
    },
    '$toolItemContainer:hover &': {
      display: 'none',
    },
  },
  toolItemText: {
    paddingLeft: '1rem',
  },
  memberIntroduction: {
    ...common.textGray500,
    ...common.middleText,
    lineHeight: 2,
    marginBottom: '3rem',
  },
  memberToolsIcon: {
    height: 120,
    width: 120,
    [theme.breakpoints.down('sm')]: {
      height: 100,
      width: 100,
    },
  },
  memberToolsContainer: {
    display: 'grid',
    position: 'relative',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    [theme.breakpoints.down('sm')]: {
      gridRow: 2,
      backgroundColor: theme.palette.common.white,
    },
  },
  memberToolItem: {
    ...common.flexColumnCenter,
    ...common.normalText,
    ...common.textWhite,
    fontSize: 20,
    rowGap: 20,
    // There are 4 items, each can be scaled and animated. In normal case, they form a united linear-gradient background.
    backgroundImage: `linear-gradient(288deg, ${theme.palette.primary.main} 6.63%, #09313A 93.45%)`,
    backgroundSize: '200%',
    transition: 'transform 0.5s, opacity 0.5s, border-radius 0.5s',
    '$memberToolsContainer:hover &': {
      transform: 'scale(85%)',
      opacity: '0.8',
      borderRadius: '28px',
      [theme.breakpoints.down('sm')]: {
        borderRadius: '14px',
      },
    },
    '&:hover': {
      ...common.textBase300,
      transform: 'scale(75%) !important',
      opacity: '1 !important',
    },
    '&:nth-child(3)': {},
    '&:nth-child(4)': {
      backgroundPosition: '-100% 0%',
    },
    '&:nth-child(5)': {
      backgroundPosition: '-0% -100%',
    },
    '&:nth-child(6)': {
      backgroundPosition: '-100% -100%',
    },
    '&$disabled': {
      color: Color(theme.palette.primary.contrastText).alpha(0.5).toString(),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
      rowGap: 1,
      transform: 'scale(85%)',
      borderRadius: '14px',
    },
  },
  memberToolItemImage: {
    transition: 'transform 0.5s ease',
    '$disabled &': {
      opacity: '0.5',
    },
    width: 120,
    height: 120,
    [theme.breakpoints.down('sm')]: {
      width: 60,
      height: 60,
    },
  },
  headerContainer: {
    display: 'grid',
    height: 640,
    gridTemplateColumns: '5fr 4fr',
    position: 'relative',
    zIndex: 0,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      height: 480,
    },
  },
  memberToolsContainerSplitVertical: {
    position: 'absolute',
    width: 1,
    top: 20,
    bottom: 20,
    left: '50%',
    right: '50%',
    zIndex: 10,
    backgroundColor: theme.palette.common.white,
  },
  memberToolsContainerSplitHorizontal: {
    position: 'absolute',
    height: 1,
    left: 20,
    right: 20,
    top: '50%',
    bottom: '50%',
    zIndex: 10,
    backgroundColor: theme.palette.common.white,
  },
  cargoTrackingSearch: {
    ...common.flexCenter,
    ...common.biggerText,
    ...common.textWhite,
    [theme.breakpoints.down('sm')]: {
      gridRow: 1,
    },
  },
  cargoTrackingSearchInner: {
    ...common.flexColumn,
    ...common.itemsCenter,
  },
  logo: {
    height: 40,
    width: 172,
    [theme.breakpoints.down('sm')]: {
      height: 18.44,
      width: 100,
    },
  },
  slogan: {
    ...common.biggerText,
    ...common.fontWeight700,
    ...common.textAlignCenter,
    marginTop: '1rem',
    marginBottom: '2rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
      marginTop: '1.25rem',
      marginBottom: '1.5rem',
    },
  },
  cargoTrackingSearchInput: {
    ...common.biggerText,
    ...common.fontWeight700,
    lineHeight: 1,
    padding: '1rem 22px !important',
    outline: 'none',
    border: 'none',
    width: 400,
    flexShrink: 1,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
      width: 190,
      padding: '0.5rem 1rem',
    },
  },
  cargoTrackingSearchInputContainer: {
    ...common.flexInline,
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
  },
  cargoTrackingSearchButton: {
    background: `linear-gradient(${theme.palette.accent.dark}, ${theme.palette.accent.main})`,
    paddingLeft: 24,
    paddingRight: 24,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
      ...common.fontWeight700,
    },
  },
  searchMultiTrackNo: {
    ...common.selfStart,
    ...common.fontWeight700,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  title: {
    ...common.normalText,
    ...common.fontWeight700,
    ...common.textAlignCenter,
    color: theme.palette.primary.main,
    fontSize: 36,
    lineHeight: 1.5,
    marginTop: '2rem',
  },
  toolsContainer: {
    display: 'grid',
    columnGap: '1rem',
    rowGap: '1rem',
    gridTemplateColumns: 'repeat(4, 1fr)',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
  },
  headerVideo: {
    position: 'absolute',
    top: 0,
    left: 0,
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    zIndex: -1,
    [theme.breakpoints.down('sm')]: {
      height: '50%',
    },
  },
  bannerContainer: {
    position: 'sticky',
    top: 70,
    zIndex: 999,
  },
  bannerRight: {
    ...common.flexCenter,
    cursor: 'pointer',
    '&:': {
      textDecoration: 'underline',
    },
  },
  bannerCollapse: {
    zIndex: 10,
    width: '100%',
    '&$animated': {
      animationName: '$shake',
      animationDuration: '0.25s',
      animationTimingFunction: 'linear',
      animationIterationCount: 1,
    },
  },
  '@keyframes shake': {
    from: {
      transform: 'translateX(10px)',
    },
    '20%': {
      transform: 'translateX(-7px)',
    },
    '40%': {
      transform: 'translateX(5px)',
    },
    '60%': {
      transform: 'translateX(-3px)',
    },
    '80%': {
      transform: 'translateX(2px)',
    },
    to: {
      transform: 'translateX(-1px)',
    },
  },
  dialog: {
    maxWidth: '960px !important',
  },
  dialogTitle: {
    ...common.textAlignCenter,
    ...common.fontWeight700,
    fontSize: '30px',
    lineHeight: '45px',
    color: theme.palette.accent.dark,
  },
  dialogSubTitle: {
    ...common.textAlignCenter,
    ...common.biggerText,
    ...common.fontWeight700,
    lineHeight: '30px',
    color: theme.palette.accent.dark,
  },
  dialogContent: {
    backgroundColor: theme.palette.grey[100],
    height: '461px',
    position: 'relative',
    ...common.flexInline,
  },
  closeButton: {
    position: 'absolute',
    right: 0,
  },
  dialogSplitVertical: {
    position: 'absolute',
    width: 1,
    top: 24,
    bottom: 36,
    left: '50%',
    right: '50%',
    zIndex: 10,
    backgroundColor: theme.palette.grey[300],
  },
  dialogContentColumnOuter: {
    flexBasis: 0,
    flex: 1,
    padding: '24px 0',
  },
  dialogContentLeftColumn: {
    marginLeft: '68px',
    rowGap: '10px',
  },
  dialogContentRightColumn: {
    marginLeft: '53px',
  },
  dialogCheckItem: {
    ...common.flexInline,
  },
  dialogCheckList: {
    margin: '25px 0',
    rowGap: '10px',
    height: '249px',
    ...common.flexColumn,
  },
  badge: {
    height: 12,
    width: 12,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.dark,
    position: 'absolute',
    top: -5,
    right: -5,
    paddingBottom: 2,
    zIndex: 2,
  },
  big: {},
  small: {},
  disabled: {},
  animated: {},
}));

interface MemberToolItemProps {
  to: string;
  title: string;
  imageSrc: string;
  imageStyle?: React.CSSProperties;
  disabled?: boolean;
  onDisableClick?: () => void;
}

const MemberToolItem: React.FC<MemberToolItemProps> = (props) => {
  const { to, imageSrc, imageStyle, title, disabled, onDisableClick } = props;

  const classes = useStyles();

  return (
    <Link
      to={disabled ? '#' : to}
      className={clsx(classes.memberToolItem, disabled && classes.disabled)}
      onClick={disabled ? onDisableClick : undefined}
    >
      <img alt={title} src={imageSrc} style={imageStyle} className={classes.memberToolItemImage} />

      <div>{title}</div>
    </Link>
  );
};

const CargoTrackingSearch: React.FC = () => {
  const history = useHistory();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <Formik
      initialValues={{
        trackNo: 'Y-',
      }}
      validate={createValidate(
        t.type({
          trackNo: requiredStringField(strings.trackNo).refine(
            (s) => s.trim() !== 'Y-',
            strings.importTrackNoError,
          ),
        }),
      )}
      onSubmit={({ trackNo }) => {
        const searchString = stringifySearch<CargoTrackingResultQuery>({ trackNos: [trackNo] });
        history.push(`${url('/utilities/CargoTracking/CargoTrackingResult')}?${searchString}`);
      }}
    >
      <Form className={classes.cargoTrackingSearch}>
        <div className={classes.cargoTrackingSearchInner}>
          <img src={d2dLogo} className={classes.logo} alt="D2D Logo" />

          <div className={clsx(classes.slogan)}>{strings.slogan}</div>

          <div className={classes.cargoTrackingSearchInputContainer}>
            <Field
              name="trackNo"
              className={clsx(classes.cargoTrackingSearchInput)}
              placeholder="Y-"
              onPaste={(event: any) => {
                let paste = event.clipboardData.getData('text');
                if (paste.startsWith('Y-')) {
                  const searchString = stringifySearch<CargoTrackingResultQuery>({
                    trackNos: [paste],
                  });
                  history.push(
                    `${url('/utilities/CargoTracking/CargoTrackingResult')}?${searchString}`,
                  );
                  event.preventDefault();
                }
              }}
            />
            <button type="submit" className={classes.cargoTrackingSearchButton}>
              {strings.track}
            </button>
          </div>

          <div style={{ height: '0.5rem' }} />

          <div className={clsx(classes.searchMultiTrackNo)}>
            <Link to={url('/utilities/CargoTracking/CargoTracking')}>
              {strings.searchMultipleTrackNos}
            </Link>
          </div>
        </div>
      </Form>
    </Formik>
  );
};

interface FAQItemProps {
  index: number;
  data: FAQItemData;
}

const FAQItem: React.FC<FAQItemProps> = (props) => {
  const { index, data } = props;

  const [expanded, setExpanded] = useState(false);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.faqItem}>
      <div className={classes.faqItemTitle} onClick={() => setExpanded(!expanded)}>
        <span style={{ paddingTop: 4 }}>
          {strings.nTh(index + 1)}
          {data.title}
        </span>
        <ExpandMoreButton expanded={expanded} />
      </div>
      <div hidden={!expanded} className={classes.faqItemContent}>
        {data.content}
      </div>
    </div>
  );
};

interface ToolItemProps {
  to: string;
  size: 'big' | 'small';
  disabled?: boolean;
  onDisableClick?: () => void;
  showBadge?: boolean;
}

const ToolItem: React.FC<React.PropsWithChildren<ToolItemProps>> = (props) => {
  const { to, children, size = 'small', disabled, onDisableClick, showBadge = false } = props;
  const classes = useStyles();

  return (
    <div style={{ position: 'relative' }}>
      <Link to={disabled ? '#' : to} onClick={disabled ? onDisableClick : undefined}>
        <div
          className={clsx(classes.toolItemContainer, classes[size], disabled && classes.disabled)}
        >
          {showBadge && <div className={classes.badge} />}
          {children}
        </div>
      </Link>
    </div>
  );
};

export const Dashboard: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);

  const isMobile = useIsMobile();
  const history = useHistory();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const user = useUser();

  const company = useCompany();

  const instructionOverview = useInstructionOverview();

  const isMember = company.data && company.data.status === '手続き完了';

  const hasApplied = company.data && company.data.status !== 'ドラフト';

  useEffect(() => {
    if (isAnimated) {
      const shakeTimer = setTimeout(() => setIsAnimated(false), 250);
      return () => clearTimeout(shakeTimer);
    }
  }, [isAnimated]);

  const onClickMembersFunction = () => {
    if (isNotificationVisible) {
      setIsAnimated(true);
    } else {
      setIsNotificationVisible(true);
    }

    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => setIsNotificationVisible(false), 5000);
  };

  const renderNotificationBanner = () => {
    const onCloseNofification = () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      setIsNotificationVisible(false);
    };

    return (
      <div className={classes.bannerContainer}>
        <Collapse
          in={isNotificationVisible}
          className={clsx(
            classes.bannerCollapse,
            isAnimated && classes.animated,
            hasApplied && classes.absoluteFill,
          )}
        >
          <NotificationBanner
            color={'warning'}
            mainText={
              hasApplied ? strings.functionIsLimitedForPending : strings.functionIsLimitedForDraft
            }
            right={
              !hasApplied && (
                <div className={classes.bannerRight}>
                  <img src={gotoIcon} style={{ height: 18, width: 20 }} />
                  <HorizontalSpace width={12} />
                  <div>{strings.applyForMembersNow}</div>
                </div>
              )
            }
            onClick={() => !hasApplied && setIsDialogOpen(true)}
            onClose={onCloseNofification}
          />
        </Collapse>
        {hasApplied && (
          <NotificationBanner color={'accent'} mainText={strings.notMemberYetForPending} />
        )}
      </div>
    );
  };

  const renderFaqs = () => {
    return (
      <div className={classes.section}>
        <div className={classes.faqSection}>
          {faqStrings.faqs().map((d, index) => (
            <FAQItem key={index} data={d} index={index} />
          ))}
        </div>
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <div className={classes.headerContainer}>
        <video
          className={classes.headerVideo}
          src={dashboardBanner}
          autoPlay
          muted
          loop
          playsInline={true}
        />

        {renderMemberTools()}
        <CargoTrackingSearch />
      </div>
    );
  };

  const renderMemberTools = () => {
    return (
      <div className={classes.memberToolsContainer}>
        {!isMobile && <div className={classes.memberToolsContainerSplitVertical} />}
        {!isMobile && <div className={classes.memberToolsContainerSplitHorizontal} />}

        <MemberToolItem
          to={url('/utilities/BookingInquiry/InquiryForm')}
          imageSrc={bookingInquiryIcon}
          title={strings.bookingInquiry}
          disabled={!isMember}
          onDisableClick={onClickMembersFunction}
        />

        <MemberToolItem
          to={url('/utilities/Bookings/CurrentBookings')}
          imageSrc={currentBookingsIcon}
          title={strings.bookingManagement}
          disabled={!isMember}
          onDisableClick={onClickMembersFunction}
        />

        <MemberToolItem
          to={url('/utilities/PriceSimulator/PriceSimulator')}
          imageSrc={priceSimulatorIcon}
          title={strings.priceSimulator}
        />

        <MemberToolItem
          to={url('/utilities/Invoice/InvoiceList')}
          imageSrc={invoiceIcon}
          title={strings.invoice}
          disabled={!isMember}
          onDisableClick={onClickMembersFunction}
        />
      </div>
    );
  };

  const renderNth = (n: number) => {
    return `${n}. `;
  };

  const renderTools = () => {
    return (
      <div className={classes.toolsContainer}>
        <ToolItem
          size="small"
          to={url('/utilities/Contact/Shippers/Shippers')}
          disabled={!isMember}
          onDisableClick={onClickMembersFunction}
        >
          <img alt="Calculator" src={shippersIcon} className={classes.toolItemNotHover} />
          <img alt="Calculator" src={shippersAccentIcon} className={classes.toolItemHover} />
          <span className={classes.toolItemText}>
            {renderNth(1)}
            {strings.shippers}
          </span>
        </ToolItem>

        <ToolItem
          size="small"
          to={url('/utilities/Contact/Consignees/Consignees')}
          disabled={!isMember}
          onDisableClick={onClickMembersFunction}
        >
          <img alt="Receivers" src={receiversIcon} className={classes.toolItemNotHover} />
          <img alt="Receivers" src={receiversAccentIcon} className={classes.toolItemHover} />
          <span className={classes.toolItemText}>
            {renderNth(2)}
            {strings.consignees}
          </span>
        </ToolItem>

        <ToolItem
          size="small"
          to={url('/utilities/Commodity/Commodities')}
          disabled={!isMember}
          onDisableClick={onClickMembersFunction}
        >
          <img alt="Commodities" src={commoditiesIcon} className={classes.toolItemNotHover} />
          <img alt="Commodities" src={commoditiesAccentIcon} className={classes.toolItemHover} />
          <span className={classes.toolItemText}>
            {renderNth(3)}
            {strings.commodities}
          </span>
        </ToolItem>

        <ToolItem
          size="small"
          to={url('/settings/Membership/PrincipalInfo')}
          disabled={!isMember}
          onDisableClick={onClickMembersFunction}
        >
          <img alt="Principal Info" src={settingsIcon} className={classes.toolItemNotHover} />
          <img alt="Principal Info" src={settingsAccentIcon} className={classes.toolItemHover} />
          <span className={classes.toolItemText}>
            {renderNth(4)}
            {strings.addStaffs}
          </span>
        </ToolItem>

        <ToolItem
          size="small"
          to={url('/utilities/Bookings/CustomsInstructionList')}
          disabled={!isMember}
          showBadge={instructionOverview.data?.customs_has_unsent}
          onDisableClick={onClickMembersFunction}
        >
          <img alt="Customs Instruction" src={customsIcon} className={classes.toolItemNotHover} />
          <img
            alt="Customs Instruction"
            src={customsAccentIcon}
            className={classes.toolItemHover}
          />
          <span className={classes.toolItemText}>
            {renderNth(5)}
            {strings.customsInstruction}
          </span>
        </ToolItem>

        <ToolItem
          size="small"
          to={url('/utilities/Bookings/DeliveryInstructionList')}
          disabled={!isMember}
          showBadge={instructionOverview.data?.delivery_has_unsent}
          onDisableClick={onClickMembersFunction}
        >
          <img alt="Delivery Instruction" src={deliveryIcon} className={classes.toolItemNotHover} />
          <img
            alt="Delivery Instruction"
            src={deliveryAccentIcon}
            className={classes.toolItemHover}
          />
          <span className={classes.toolItemText}>
            {renderNth(6)}
            {strings.deliveryInstruction}
          </span>
        </ToolItem>

        <ToolItem
          size="small"
          to={url('/utilities/BlanketInsurance/BlanketInsurances')}
          disabled={!isMember}
          onDisableClick={onClickMembersFunction}
        >
          <img
            alt="Blanket Insurance"
            src={blanketInsuranceIcon}
            className={classes.toolItemNotHover}
          />
          <img
            alt="Blanket Insurance"
            src={blanketInsuranceAccentIcon}
            className={classes.toolItemHover}
          />
          <span className={classes.toolItemText}>
            {renderNth(7)}
            {instructionStrings.blanketInsuranceList}
          </span>
        </ToolItem>
      </div>
    );
  };

  const renderGoldenWeek2023Banner = () => {
    return (
      <NotificationBanner
        color="warning"
        mainText={strings.goldenWeek2023Notification}
        onClick={() => history.push(url('/utilities/Notifications/GoldenWeek2023'))}
      />
    );
  };

  return (
    <Layout
      loading={user.isLoading || company.isLoading || instructionOverview.isLoading}
      title={strings.title}
      errors={[user.error, company.error, instructionOverview.error]}
      classes={{
        background: classes.background,
      }}
    >
      {() => (
        <div>
          <div
            style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              padding: '16px 20px',
              color: '#664d03',
              fontSize: 14,
              lineHeight: 1.8,
              textAlign: 'center',
            }}
          >
            <strong>【重要】システム移行のお知らせ</strong>
            本システムは新システム（
            <a
              href="https://online.fcdf.jp"
              target="_blank"
              rel="noreferrer"
              style={{ color: '#0d6efd', textDecoration: 'underline' }}
            >
              https://online.fcdf.jp
            </a>
            ）に移行中です。
            2026年5月以降にご利用を行われる場合は、
            <a
              href="https://d2dasia.com/about-d2d/"
              target="_blank"
              rel="noreferrer"
              style={{ color: '#0d6efd', textDecoration: 'underline' }}
            >
              こちらのフォーム
            </a>
            よりご連絡ください。
          </div>
          <MemberIntroDialog isOpen={isDialogOpen} setIsOpen={setIsDialogOpen} />
          {!isMember && renderNotificationBanner()}
          {dayjs().format('YYYY-MM-DD') <= '2023-05-03' && renderGoldenWeek2023Banner()}
          {renderHeader()}
          <div className={classes.outer}>
            <div className={classes.inner}>
              <h2 className={classes.title}>{strings.toolsTitle}</h2>
              {renderTools()}
            </div>
          </div>

          <div className={classes.outerFaq}>
            <div className={classes.inner}>
              <h2 className={classes.title}>{strings.faqsTitle}</h2>
              {renderFaqs()}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
